import type { InvestmentResponse } from '@app/models/admin.model';
import { InvestmentStatus } from '@app/models/investment.models';
import { RowStatus } from '@app/models/status.model';

export type BadgeView = {
  labelKey?: string;
  badgeClass?: string;
  infoTooltipKey?: string;
};

export function createBadgeHelpers<K extends PropertyKey>(
  viewMap: Record<K, BadgeView>,
  fallback: K
) {
  const toView = (k?: K) => viewMap[(k ?? fallback) as K];
  const badgeClass = (k?: K) => toView(k).badgeClass;
  const labelKey = (k?: K) => toView(k).labelKey;
  return { toView, badgeClass, labelKey };
}

const STATUS_MAP: Record<RowStatus, BadgeView> = {
  approved: {
    badgeClass: 'badge-subtle-success',
    labelKey: 'admin.common.approved',
  },
  declined: {
    badgeClass: 'badge-subtle-danger',
    labelKey: 'admin.common.declined',
  },
  pending: {
    badgeClass: 'badge-subtle-default',
    labelKey: 'admin.common.pending',
  },
};

export const statusBadge = createBadgeHelpers<RowStatus>(STATUS_MAP, 'pending');

type BoolKey = 'true' | 'false' | 'unknown';
const BOOL_MAP: Record<BoolKey, BadgeView> = {
  true: { badgeClass: 'badge-subtle-success', labelKey: 'general.yes' },
  false: { badgeClass: 'badge-subtle-warning', labelKey: 'general.no' },
  unknown: { badgeClass: 'badge-subtle-default', labelKey: 'general.unknown' },
};

export const boolBadge = createBadgeHelpers<BoolKey>(BOOL_MAP, 'unknown');
export const boolToKey = (v?: boolean | null | undefined): BoolKey =>
  v === true ? 'true' : v === false ? 'false' : 'unknown';

export type DecisionStatus = 'approved' | 'declined' | 'pending';

export const connectionToStatus = (
  value: boolean | null | undefined
): DecisionStatus =>
  value === true ? 'approved' : value === false ? 'declined' : 'pending';

export function toInvestmentBadgeView(name?: string): BadgeView {
  const key = (name ?? '').trim();
  const labelKey = `admin.investments.investmentStatus.${key}`;

  const WAITING = new Set(['Sent', 'PendingReview', 'UnderReview']);
  const SUCCESS = new Set(['ActiveFunded', 'Completed']);
  const SECONDARY = new Set(['Accepted']);
  const DANGER = new Set(['Rejected', 'Cancelled', 'Refounded']);

  const view: BadgeView = {
    labelKey,
    badgeClass: undefined,
    infoTooltipKey: undefined,
  };

  if (DANGER.has(key)) view.badgeClass = 'badge-subtle-danger';
  else if (SECONDARY.has(key)) view.badgeClass = 'badge-subtle-secondary';
  else if (SUCCESS.has(key)) view.badgeClass = 'badge-subtle-success';
  else if (WAITING.has(key)) view.badgeClass = 'badge-subtle-warning';

  return view;
}

export type InvestmentRowActionType =
  | 'accept_reject'
  | 'approve'
  | 'pending_countdown'
  | null;

export function getActionType(
  inv: InvestmentResponse
): InvestmentRowActionType {
  const status = inv.status;
  if (status === InvestmentStatus.UnderReview) return 'accept_reject';
  if (status === InvestmentStatus.Accepted) return 'approve';
  if (status === InvestmentStatus.PendingReview) return 'pending_countdown';
  return null;
}
